import React, { useEffect, useMemo, useState } from 'react'

import { X } from 'lucide-react'
import { useForm, useWatch } from 'react-hook-form'

import { useUpdateBrandProfile } from '@/core/hooks/account/useAccount'
import { useRefetchAccountData } from '@/core/hooks/account/useRefetchAccountData'
import { useBusinessCategories } from '@/core/hooks/misc/useBusinessCategories'
import { useDemographicAgeGroups } from '@/core/hooks/misc/useDemographicAgeGroups'
import { useDemographicGenders } from '@/core/hooks/misc/useDemographicGenders'
import { useDemographicSocioeconomicGroups } from '@/core/hooks/misc/useDemographicSocioeconomicGroups'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { useBrandIdentities } from '@/core/hooks/useBrandIdentities'
import { useTranslation } from '@/core/i18n/useTranslation'
import { useBrandProfileState } from '@/features/account/hooks/useBrandProfileState'
import { Badge } from '@/shared/components/atoms/Badge/Badge'
import { Button } from '@/shared/components/atoms/Button'
import { Separator } from '@/shared/components/atoms/Separator'
import {
	BottomSheet,
	BottomSheetContent,
	BottomSheetTitle,
} from '@/shared/components/molecules/BottomSheet/BottomSheet'
import { useNotificationContext } from '@/shared/context/NotificationProvider'
import { useCurrentAccount } from '@/shared/hooks/useCurrentAccount'

import { AudienceSection } from './BrandProfile/AudienceSection'
import { BrandSection } from './BrandProfile/BrandSection'
import {
	mapBrandIdentitiesToTags,
	mapDemographicGroupsToTags,
	getAccountFormDefaults,
} from './BrandProfile/utils'

interface BrandProfileProps {
	onMobileEditingChange?: (isEditing: boolean) => void
	requestMobileEdit?: boolean
}

interface BrandProfileFormData {
	parent_business_category_id: number | null
	business_category_id: number | null
	business_category_description?: string | null
}

const BrandProfile: React.FC<BrandProfileProps> = ({
	onMobileEditingChange,
	requestMobileEdit,
}) => {
	const { t } = useTranslation('common')
	const { showNotification } = useNotificationContext()

	// Use account data from API if selected, otherwise use user's account
	const currentAccount = useCurrentAccount() || undefined

	// Use the centralized refetch hook
	const refetchCurrentAccountData = useRefetchAccountData()

	const { state, dispatch, reset } = useBrandProfileState(currentAccount)
	const [isMobileEditing, setIsMobileEditing] = useState(false)

	useEffect(() => {
		onMobileEditingChange?.(isMobileEditing)
	}, [isMobileEditing, onMobileEditingChange])

	useEffect(() => {
		if (requestMobileEdit) {
			setIsMobileEditing(true)
		}
	}, [requestMobileEdit])

	// Fetch data from APIs
	const {
		data: businessCategories = [],
		isLoading: isLoadingBusinessCategories,
	} = useBusinessCategories()
	const { data: brandIdentitiesResponse } = useBrandIdentities()
	const brandIdentities = brandIdentitiesResponse?.data || []

	const { data: demographicAgeGroups = [] } = useDemographicAgeGroups()
	const { data: demographicGenders = [] } = useDemographicGenders()
	const { data: demographicSocioeconomicGroups = [] } =
		useDemographicSocioeconomicGroups()

	// Initialize form with default values from account data
	// Use accountIdToUse as key to force form reset when account changes
	const form = useForm<BrandProfileFormData>({
		defaultValues: getAccountFormDefaults(currentAccount),
	})

	// Reset form when account changes
	useEffect(() => {
		form.reset(getAccountFormDefaults(currentAccount))
	}, [currentAccount])

	const formValues = useWatch({
		control: form.control,
	})

	// Update brand profile mutation using the new hook
	const updateBrandProfileMutation = useUpdateBrandProfile({
		accountId: currentAccount?.id || 0,
		config: useAccountConfig({}),
		options: {
			onSuccess: () => {
				// Refetch user data to update Redux state with new account info (only if not viewing selected account)
				refetchCurrentAccountData()

				// Show success notification
				showNotification({
					type: 'success',
					message: t('account.brand_profile.update_success'),
				})
			},
			onError: (error: any) => {
				const data = error?.response?.data
				let description: React.ReactNode =
					data?.message || t('account.brand_profile.update_error_description')

				if (data?.errors) {
					const errorMessages = Object.values(data.errors).flat() as string[]
					if (errorMessages.length > 0) {
						description = (
							<div className="flex flex-col gap-1">
								<ul className="list-disc pl-4">
									{errorMessages.map((msg, index) => (
										<li key={index}>{msg}</li>
									))}
								</ul>
							</div>
						)
					}
				}

				showNotification({
					type: 'error',
					message: t('account.brand_profile.update_error'),
					description,
				})
			},
		},
	})

	const selectedSubcategory = businessCategories.find(
		(c) => c.id === formValues.business_category_id
	)
	const requiresDescription = selectedSubcategory?.requiresDescription ?? false

	const canSave = React.useMemo(() => {
		return (
			!!formValues.business_category_id &&
			(!requiresDescription ||
				(typeof formValues.business_category_description === 'string' &&
					formValues.business_category_description.trim().length > 0)) &&
			state.selectedBrandTags.length > 0 &&
			state.selectedAgeTags.length > 0 &&
			!!state.selectedGender &&
			state.selectedSocioeconomicTags.length > 0
		)
	}, [
		formValues,
		requiresDescription,
		state.selectedBrandTags.length,
		state.selectedAgeTags.length,
		state.selectedGender,
		state.selectedSocioeconomicTags.length,
	])

	// Map data to display tags
	const brandTags = mapBrandIdentitiesToTags(brandIdentities)
	const ageTags = mapDemographicGroupsToTags(demographicAgeGroups, t)
	const genderTags = mapDemographicGroupsToTags(demographicGenders)
	const socioeconomicTags = mapDemographicGroupsToTags(
		demographicSocioeconomicGroups,
		t
	)

	const handleSave = async (): Promise<boolean> => {
		const formData = form.getValues()

		const updateData = {
			parent_business_category_id: formData.parent_business_category_id,
			business_category_id: formData.business_category_id,
			business_category_description:
				formData.business_category_description ?? null,
			brand_identities: state.selectedBrandTags,
			demographic_ages: state.selectedAgeTags,
			demographic_gender: state.selectedGender,
			demographic_socioeconomics: state.selectedSocioeconomicTags,
		}

		try {
			await updateBrandProfileMutation.mutateAsync(updateData)
			return true
		} catch (_error) {
			return false
		}
	}

	const handleCancel = () => {
		reset()
		form.reset(getAccountFormDefaults(currentAccount))
	}

	const handleCloseMobileEdit = () => {
		handleCancel()
		setIsMobileEditing(false)
	}

	const isSaving = updateBrandProfileMutation.isPending

	const selectedBrandLabels = useMemo(
		() =>
			brandTags
				.filter((tag) => state.selectedBrandTags.includes(tag.id))
				.map((tag) => tag.display),
		[brandTags, state.selectedBrandTags]
	)
	const selectedAgeLabels = useMemo(
		() =>
			ageTags
				.filter((tag) => state.selectedAgeTags.includes(tag.key))
				.map((tag) => tag.display),
		[ageTags, state.selectedAgeTags]
	)
	const selectedGenderLabel = useMemo(
		() =>
			genderTags.find((tag) => tag.key === state.selectedGender)?.display ||
			'-',
		[genderTags, state.selectedGender]
	)
	const selectedSocioeconomicLabels = useMemo(
		() =>
			socioeconomicTags
				.filter((tag) => state.selectedSocioeconomicTags.includes(tag.key))
				.map((tag) => tag.display),
		[socioeconomicTags, state.selectedSocioeconomicTags]
	)

	const selectedParentBusinessCategory = useMemo(
		() =>
			businessCategories.find(
				(category) => category.id === formValues.parent_business_category_id
			)?.name || '-',
		[businessCategories, formValues.parent_business_category_id]
	)
	const selectedBusinessSubcategory = useMemo(
		() =>
			businessCategories.find(
				(category) => category.id === formValues.business_category_id
			)?.name || '-',
		[businessCategories, formValues.business_category_id]
	)
	const handleBrandTagToggle = (id: number) => {
		const isCurrentlySelected = state.selectedBrandTags.includes(id)
		if (!isCurrentlySelected && state.selectedBrandTags.length >= 3) {
			showNotification({
				type: 'error',
				message: t('account.brand_profile.max_selection_error'),
			})
			return
		}
		dispatch({ type: 'TOGGLE_BRAND_TAG', id })
	}

	return (
		<div className="space-y-8">
			<div className="hidden lg:block space-y-8">
				<BrandSection
					control={form.control}
					setValue={form.setValue}
					businessCategories={businessCategories}
					isLoadingBusinessCategories={isLoadingBusinessCategories}
					brandTags={brandTags}
					selectedBrandTags={state.selectedBrandTags}
					onBrandTagToggle={handleBrandTagToggle}
				/>

				<Separator />

				<AudienceSection
					ageTags={ageTags}
					genderTags={genderTags}
					socioeconomicTags={socioeconomicTags}
					selectedAgeTags={state.selectedAgeTags}
					selectedGender={state.selectedGender}
					selectedSocioeconomicTags={state.selectedSocioeconomicTags}
					onAgeTagToggle={(tag) => dispatch({ type: 'TOGGLE_AGE_TAG', tag })}
					onGenderSelect={(tag) => dispatch({ type: 'SELECT_GENDER', tag })}
					onSocioeconomicToggle={(tag) =>
						dispatch({ type: 'TOGGLE_SOCIOECONOMIC_TAG', tag })
					}
				/>

				<div className="flex gap-3 pt-4">
					<Button
						className="rounded-full"
						variant="outline"
						onClick={handleCancel}
						disabled={isSaving}
					>
						{t('actions.cancel')}
					</Button>
					<Button
						className="rounded-full"
						onClick={handleSave}
						disabled={isSaving || !canSave}
					>
						{isSaving ? t('actions.saving') : t('actions.save')}
					</Button>
				</div>
			</div>

			<div className="lg:hidden">
				<div className="space-y-8 pb-24">
					<div className="space-y-6">
						<h2 className="text-xl font-semibold">
							{t('account.brand.title')}
						</h2>
						<div className="space-y-4">
							<div>
								<p className="text-muted-foreground text-sm">
									{t('account.brand.business_category')}
								</p>
								<p className="text-lg">{selectedParentBusinessCategory}</p>
							</div>
							<div>
								<p className="text-muted-foreground text-sm">
									{t('account.brand.business_subcategory')}
								</p>
								<p className="text-lg">{selectedBusinessSubcategory}</p>
							</div>
							<div>
								<p className="text-muted-foreground text-sm mb-2">
									{t('account.brand.brand_profile')}
								</p>
								<div className="flex flex-wrap gap-2">
									{selectedBrandLabels.length > 0 ? (
										selectedBrandLabels.map((label) => (
											<Badge
												key={label}
												variant="outline"
												className="rounded-full px-3 py-1.5 text-sm bg-input text-foreground border-border"
											>
												{label}
											</Badge>
										))
									) : (
										<p className="text-lg">-</p>
									)}
								</div>
							</div>
						</div>
					</div>

					<Separator />

					<div className="space-y-6">
						<h2 className="text-xl font-semibold">
							{t('account.brand.audience')}
						</h2>
						<div className="space-y-4">
							<div>
								<p className="text-muted-foreground text-sm mb-2">
									{t('account.brand.age')}
								</p>
								<div className="flex flex-wrap gap-2">
									{selectedAgeLabels.length > 0 ? (
										selectedAgeLabels.map((label) => (
											<Badge
												key={label}
												variant="outline"
												className="rounded-full px-3 py-1.5 text-sm bg-input text-foreground border-border"
											>
												{label}
											</Badge>
										))
									) : (
										<p className="text-lg">-</p>
									)}
								</div>
							</div>
							<div>
								<p className="text-muted-foreground text-sm mb-2">
									{t('account.brand.gender')}
								</p>
								<div className="flex flex-wrap gap-2">
									<Badge
										variant="outline"
										className="rounded-full px-3 py-1.5 text-sm bg-input text-foreground border-border"
									>
										{selectedGenderLabel}
									</Badge>
								</div>
							</div>
							<div>
								<p className="text-muted-foreground text-sm mb-2">
									{t('account.brand.socioeconomic_level')}
								</p>
								<div className="flex flex-wrap gap-2">
									{selectedSocioeconomicLabels.length > 0 ? (
										selectedSocioeconomicLabels.map((label) => (
											<Badge
												key={label}
												variant="outline"
												className="rounded-full px-3 py-1.5 text-sm bg-input text-foreground border-border"
											>
												{label}
											</Badge>
										))
									) : (
										<p className="text-lg">-</p>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>

				<BottomSheet
					open={isMobileEditing}
					onOpenChange={(open) => {
						if (!open) {
							handleCloseMobileEdit()
							return
						}
						setIsMobileEditing(true)
					}}
				>
					<BottomSheetContent
						variant="card"
						className="border-border h-auto max-h-[90vh] px-0 pb-8"
						aria-describedby={undefined}
					>
						<div className="px-5 pt-2 space-y-8">
							<div className="flex items-center justify-between">
								<BottomSheetTitle className="text-xl md:text-2xl font-semibold">
									{t('account.brand_profile.mobile.edit_profile')}
								</BottomSheetTitle>
								<Button
									variant="ghost"
									size="icon"
									onClick={handleCloseMobileEdit}
									className="h-10 w-10 rounded-full"
									aria-label={t('actions.close')}
								>
									<X className="w-5 h-5" />
								</Button>
							</div>
							<Separator />

							<BrandSection
								control={form.control}
								setValue={form.setValue}
								businessCategories={businessCategories}
								isLoadingBusinessCategories={isLoadingBusinessCategories}
								brandTags={brandTags}
								selectedBrandTags={state.selectedBrandTags}
								onBrandTagToggle={handleBrandTagToggle}
							/>

							<Separator />

							<AudienceSection
								ageTags={ageTags}
								genderTags={genderTags}
								socioeconomicTags={socioeconomicTags}
								selectedAgeTags={state.selectedAgeTags}
								selectedGender={state.selectedGender}
								selectedSocioeconomicTags={state.selectedSocioeconomicTags}
								onAgeTagToggle={(tag) =>
									dispatch({ type: 'TOGGLE_AGE_TAG', tag })
								}
								onGenderSelect={(tag) =>
									dispatch({ type: 'SELECT_GENDER', tag })
								}
								onSocioeconomicToggle={(tag) =>
									dispatch({ type: 'TOGGLE_SOCIOECONOMIC_TAG', tag })
								}
							/>

							<Button
								className="w-full rounded-full"
								onClick={async () => {
									const isSaved = await handleSave()
									if (isSaved) {
										setIsMobileEditing(false)
									}
								}}
								disabled={isSaving || !canSave}
							>
								{isSaving ? t('actions.saving') : t('actions.save')}
							</Button>
						</div>
					</BottomSheetContent>
				</BottomSheet>
			</div>
		</div>
	)
}

export default BrandProfile
